import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { Receipt } from '@phosphor-icons/react';

const formatCurrency = (v) => `${parseFloat(v || 0).toFixed(2)} \u20AC`;

const formatDate = (d) => {
  if (!d) return '-';
  const parts = d.split('-');
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export const ReceiptsView = ({ anno }) => {
  const [receipts, setReceipts] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await api.getReceipts(anno ? { anno } : {});
        setReceipts(response.data.records);
        setTotalAmount(response.data.total_amount);
      } catch (error) {
        console.error('Error loading receipts:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [anno]);

  const totalAltro = receipts.reduce((s, r) => s + (parseFloat(r.altro) || 0), 0);
  const totalPagato = receipts.reduce((s, r) => s + (parseFloat(r.totale_pagato) || 0), 0);

  return (
    <div>
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-4 text-center" data-testid="receipts-count-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-1">Ricevute Emesse</div>
          <div className="text-2xl font-bold font-['Work_Sans']">{receipts.length}</div>
        </div>
        <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-sm p-4 text-center" data-testid="receipts-total-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#4338CA] mb-1">Totale Addebiti</div>
          <div className="text-2xl font-bold font-['Work_Sans'] text-[#4338CA]">{formatCurrency(totalAmount)}</div>
        </div>
        <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-sm p-4 text-center" data-testid="receipts-total-altro-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#92400E] mb-1">Totale Altro</div>
          <div className="text-2xl font-bold font-['Work_Sans'] text-[#92400E]">{formatCurrency(totalAltro)}</div>
        </div>
        <div className="bg-[#D1FAE5] border border-[#6EE7B7] rounded-sm p-4 text-center" data-testid="receipts-total-pagato-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#065F46] mb-1">Totale Pagato</div>
          <div className="text-2xl font-bold font-['Work_Sans'] text-[#065F46]">{formatCurrency(totalPagato)}</div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
      ) : receipts.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm">
          <Receipt size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <p className="text-[#52525B]">Nessuna ricevuta emessa</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-x-auto">
          <table className="w-full" data-testid="receipts-table">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Documento</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Nr. Ricevuta</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Nr. Fattura</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Data</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Totale Addebiti</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#92400E]">Altro</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#065F46]">Totale Pagato</th>
              </tr>
            </thead>
            <tbody>
              {receipts.map(r => (
                <tr key={r.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`receipt-row-${r.id}`}>
                  <td className="px-3 py-1.5 text-xs text-center uppercase font-semibold text-[#52525B]">{r.tipo_documento || 'ricevuta'}</td>
                  <td className="px-3 py-1.5 text-sm font-medium">{r.nr_ricevuta || '-'}</td>
                  <td className="px-3 py-1.5 text-sm text-[#52525B]">{r.nr_fattura || '-'}</td>
                  <td className="px-3 py-1.5 text-sm text-[#52525B]">{formatDate(r.data_ricevuta)}</td>
                  <td className="px-3 py-1.5 text-sm text-right font-semibold font-['Work_Sans']">{formatCurrency(r.totale)}</td>
                  <td className="px-3 py-1.5 text-sm text-right font-semibold font-['Work_Sans'] text-[#92400E]">{formatCurrency(r.altro || 0)}</td>
                  <td className="px-3 py-1.5 text-sm text-right font-bold font-['Work_Sans'] text-[#065F46]">{formatCurrency(r.totale_pagato)}</td>
                </tr>
              ))}
              {/* Totale row */}
              <tr className="bg-[#FAFAFA] border-t-2 border-[#E4E4E7]">
                <td colSpan={4} className="px-3 py-2 text-sm font-bold uppercase">Totale</td>
                <td className="px-3 py-2 text-sm text-right font-bold font-['Work_Sans']">{formatCurrency(totalAmount)}</td>
                <td className="px-3 py-2 text-sm text-right font-bold font-['Work_Sans'] text-[#92400E]">{formatCurrency(totalAltro)}</td>
                <td className="px-3 py-2 text-sm text-right font-bold font-['Work_Sans'] text-[#065F46]">{formatCurrency(totalPagato)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
